import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-45">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={onClose} data-id="element-46" />
          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl md:w-full md:max-h-[80vh] bg-[#111827] border border-[#ffffff]/10 rounded-2xl z-50 flex flex-col overflow-hidden shadow-2xl" data-id="element-47">
            {/* Header */}
            <div className="flex items-center justify-between p-5 md:p-6 border-b border-[#ffffff]/5 flex-shrink-0" data-id="element-48">
              <h3 className="text-lg md:text-xl font-semibold text-[#f5f0e8] font-playfair" data-id="element-49">
                {title}
              </h3>
              <button onClick={onClose} className="text-[#8b8178] hover:text-[#e8e0d4] transition-colors p-1" data-id="element-50">
                <XIcon className="w-5 h-5" data-id="element-51" />
              </button>
            </div>
            {/* Content */}
            <div className="p-5 md:p-6 overflow-y-auto flex-1 text-[#8b8178] text-sm leading-relaxed space-y-4" data-id="element-52">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <>
      <footer className="bg-[#05070d] border-t border-[#ffffff]/5 py-8 md:py-12 px-5 md:px-6" data-id="element-53">
        <div className="max-w-3xl mx-auto" data-id="element-54">
          {/* Brand */}
          <div className="text-center mb-5 md:mb-6" data-id="element-55">
            <p className="text-[#8b8178] text-sm" data-id="element-56">
              A digital product by{' '}
              <a href="https://plenava.com" target="_blank" rel="noopener noreferrer" className="text-[#c9a96e] hover:text-[#d4b87a] transition-colors" data-id="element-57">
                Plenava.com
              </a>
            </p>
          </div>

          {/* Legal Links */}
          <div className="flex items-center justify-center gap-6 md:gap-8 mb-6" data-id="element-58">
            <button onClick={() => setPrivacyOpen(true)} className="text-[#8b8178] hover:text-[#e8e0d4] text-sm transition-colors underline underline-offset-2 decoration-[#8b8178]/30" data-id="element-59">
              Privacy Policy
            </button>
            <span className="text-[#ffffff]/10" data-id="element-60">|</span>
            <button onClick={() => setTermsOpen(true)} className="text-[#8b8178] hover:text-[#e8e0d4] text-sm transition-colors underline underline-offset-2 decoration-[#8b8178]/30" data-id="element-61">
              Terms of Sale
            </button>
          </div>

          {/* Copyright */}
          <div className="text-center pt-4 border-t border-[#ffffff]/5" data-id="element-62">
            <p className="text-[#8b8178] text-xs opacity-60" data-id="element-63">
              © {new Date().getFullYear()} Plenava.com — All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-64">
        <p data-id="element-65">
          Plenava.com ("we", "us", or "our") is committed to protecting your
          privacy. This Privacy Policy explains how we collect, use, and
          safeguard your personal information when you purchase and download our
          digital products.
        </p>
        <p data-id="element-66">
          <strong className="text-[#e8e0d4]" data-id="element-67">Information We Collect:</strong>{' '}
          When you make a purchase, we collect your name, email address, and
          payment information. Payment processing is handled by secure
          third-party providers — we do not store your credit card details.
        </p>
        <p data-id="element-68">
          <strong className="text-[#e8e0d4]" data-id="element-69">
            How We Use Your Information:
          </strong>{' '}
          We use your information solely to process your order, deliver your
          digital product (PDF), and send transactional communications related
          to your purchase. We will not sell, rent, or share your personal data
          with third parties for marketing purposes.
        </p>
        <p data-id="element-70">
          <strong className="text-[#e8e0d4]" data-id="element-71">Cookies:</strong> Our website may
          use essential cookies to ensure proper functionality. We do not use
          tracking cookies for advertising purposes.
        </p>
        <p data-id="element-72">
          <strong className="text-[#e8e0d4]" data-id="element-73">Your Rights:</strong> You have the
          right to access, correct, or delete your personal data at any time. To
          exercise these rights, contact us at the email address provided on
          Plenava.com.
        </p>
        <p data-id="element-74">
          <strong className="text-[#e8e0d4]" data-id="element-75">Data Retention:</strong> We retain
          your purchase records and email for the duration necessary to fulfill
          our legal and business obligations. You may request deletion at any
          time.
        </p>
      </Modal>

      {/* Terms of Sale Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Sale — Digital eBook Products" data-id="element-76">
        <p data-id="element-77">
          By purchasing "The 3AM Loop Breaker (7-Day Reset)" or any digital
          product from Plenava.com, you agree to the following terms:
        </p>
        <p data-id="element-78">
          <strong className="text-[#e8e0d4]" data-id="element-79">Product Format:</strong> This is a
          digital product delivered as a downloadable PDF file. No physical
          product will be shipped. You will receive instant access to the
          download link upon successful payment.
        </p>
        <p data-id="element-80">
          <strong className="text-[#e8e0d4]" data-id="element-81">License:</strong> Your purchase
          grants you a personal, non-transferable, non-exclusive license to use
          the content for your own private purposes. You may not redistribute,
          resell, copy, or share the PDF with others.
        </p>
        <p data-id="element-82">
          <strong className="text-[#e8e0d4]" data-id="element-83">Pricing:</strong> All prices are
          displayed in Euros (€) and include applicable taxes where required.
          The discounted price is valid at the time of purchase and may change
          without prior notice.
        </p>
        <p data-id="element-84">
          <strong className="text-[#e8e0d4]" data-id="element-85">Refund Policy:</strong> Due to the
          nature of digital products, all sales are final once the download link
          has been delivered. If you experience a technical issue preventing
          access to your file, please contact us and we will resolve it
          promptly.
        </p>
        <p data-id="element-86">
          <strong className="text-[#e8e0d4]" data-id="element-87">Disclaimer:</strong> The content in
          this guide is for informational and educational purposes only. It is
          not a substitute for professional medical advice, diagnosis, or
          treatment. If you suspect a medical sleep disorder, please consult a
          qualified healthcare provider.
        </p>
        <p data-id="element-88">
          <strong className="text-[#e8e0d4]" data-id="element-89">Governing Law:</strong> These terms
          are governed by the laws applicable in the jurisdiction where
          Plenava.com operates. Any disputes will be resolved in accordance with
          those laws.
        </p>
        <p data-id="element-90">
          For questions or support, please contact us through the contact
          information available at{' '}
          <a href="https://plenava.com" target="_blank" rel="noopener noreferrer" className="text-[#c9a96e] hover:text-[#d4b87a] transition-colors" data-id="element-91">
            Plenava.com
          </a>
          .
        </p>
      </Modal>
    </>;
}